import { useAppTranslations } from '../../../utils/hooks/useAppTranslations'
import React, { Dispatch, useState } from 'react'
import { FormikValues } from 'formik'
import { FormikStepper } from '../../../components/form/wizzard/FormikStepper'
import {
  CreatePositionMutation,
  PositionCreateInput,
  useCreatePositionMutation,
  WebsiteQuery,
} from '../../../__generated__/graphql'
import { buildPositionCreateFormInitialValues } from './buildPositionCreateInitialValues'
import { toPositionCreateInput } from './toPositionCreateInput'
import CreatePositionDetailsStep, { summarySchema } from './steps/CreatePositionDetailsStep'
import CreatePositionSettingsStep, { settingsSchema } from './steps/CreatePositionSettingsStep'
import { Edit } from '@mui/icons-material'
import { useNavigation } from '../../../utils/hooks/useNavigation'
import CreatePositionWebsiteStep, { websiteSchema } from './steps/CreatePositionWebsiteStep'
import { useFormDialogContext } from '../../../components/layout/FormDialog'
import { useSidebarContext } from '../../../utils/providers/sidebarContext'
import { useUserPermissions } from '../../company/userPermissions'

const CreatePositionStepper: React.FC<{
  website?: WebsiteQuery['website']
  setOpen: Dispatch<React.SetStateAction<boolean>>
  onSuccess?: () => void
}> = ({ website, setOpen, onSuccess }) => {
  const [, setHasChanged] = useFormDialogContext()
  const t = useAppTranslations()
  const [error, setError] = React.useState(undefined)
  const [createdPosition, setCreatedPosition] = useState<CreatePositionMutation['createPosition'] | null>()
  const [createPosition] = useCreatePositionMutation()
  const { reloadItems } = useSidebarContext()
  const navigation = useNavigation()
  const { superAdmin } = useUserPermissions()

  const initialValues: PositionCreateInput = Object.assign(
    {},
    buildPositionCreateFormInitialValues(website?.iabCategory),
    { websiteId: website?.id || '' }
  )

  const handleSubmit = async (values: FormikValues): Promise<void> => {
    await createPosition({
      variables: {
        newPosition: toPositionCreateInput(values, values.websiteId),
      },
    })
      .then((res) => {
        const createdPosition = res?.data?.createPosition
        if (createdPosition) {
          reloadItems()
          setCreatedPosition(createdPosition)
        }
      })
      .catch((err) => setError(err))
  }

  const successButton = {
    label: t('position.goToPosition', { positionName: createdPosition?.name }),
    startIcon: <Edit />,
    onClick: () => {
      if (createdPosition) {
        navigation.goToPosition(createdPosition.website.publisher.slug, String(createdPosition.id))
        onSuccess?.()
        setOpen(false)
      }
    },
  }

  return (
    <FormikStepper
      initialValues={initialValues}
      onSubmit={handleSubmit}
      error={error}
      setError={setError}
      enableReinitialize={true}
      successMessage={t('position.created')}
      setOpen={setOpen}
      successActionButton={successButton}
      mergeSteps={true}
      setHasChanged={setHasChanged}
    >
      {!initialValues.websiteId && (
        <CreatePositionWebsiteStep validationSchema={websiteSchema} label={t('position.create.websiteStep.title')} />
      )}

      <CreatePositionDetailsStep validationSchema={summarySchema} label={t('commons.details')} />
      {superAdmin && <CreatePositionSettingsStep validationSchema={settingsSchema} label={t('commons.settings')} />}
    </FormikStepper>
  )
}

export default CreatePositionStepper
